// Company detail — the drill-in page for internal admins (VerifyU Ops).
//
// Industry-standard org-scoped provisioning: instead of a global "invite a user" screen
// (where an internal admin could create a company-less user by mistake), you navigate
// INTO a company and manage its people from here. The Members and Invites tabs are the
// existing screens, scoped to this account — so every invite necessarily lands in THIS
// company. Reached from Accounts → click a company.
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { App, Card, Tabs, Tag, Typography, Button, Space, Skeleton, Select, Switch } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { accountApi, brandsApi, usersApi } from "../../console/consoleApi";
import { useStepUp } from "../../console/useStepUp";
import { errorMessage } from "../../console/consoleUtils";
import ConsoleUsers from "./ConsoleUsers";
import ConsoleInvites from "./ConsoleInvites";

const { Title, Text } = Typography;

const isCancelled = (err) => err?.message === "STEP_UP_CANCELLED";
const normalizeDomains = (vals) => [
  ...new Set((vals ?? []).map((v) => v.trim().toLowerCase().replace(/^@/, "")).filter(Boolean)),
];

export default function ConsoleAccountDetail() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { withStepUp, modal } = useStepUp();
  const [account, setAccount] = useState(null);
  const [brands, setBrands] = useState([]);
  const [savingBrand, setSavingBrand] = useState(false);
  const [domains, setDomains] = useState([]); // pending edit of the allowlist
  const [savingDomains, setSavingDomains] = useState(false);
  // VER-222 entitlement editor: the saved set + the full catalog + the pending edit.
  const [catalog, setCatalog] = useState([]); // all API names (internal admin sees full catalog)
  const [ent, setEnt] = useState(null); // saved { allApis, prodEnabled, apiCollections }
  const [entDraft, setEntDraft] = useState(null); // pending edit
  const [savingEnt, setSavingEnt] = useState(false);

  const load = useCallback(async () => {
    setAccount(null);
    try {
      const [accRes, brandRes, entRes, catRes] = await Promise.all([
        accountApi.getOne(accountId),
        brandsApi.list(),
        accountApi.getEntitlements(accountId),
        usersApi.apiCollections(),
      ]);
      setAccount(accRes.data.data.account);
      setDomains(accRes.data.data.account.allowedDomains ?? []);
      setBrands(brandRes.data.data.brands);
      setEnt(entRes.data.data);
      setEntDraft(entRes.data.data);
      setCatalog(catRes.data.data.apiCollections ?? []);
    } catch (err) {
      message.error(errorMessage(err));
    }
  }, [accountId, message]);

  const saveEntitlements = async () => {
    setSavingEnt(true);
    try {
      const res = await withStepUp((token) =>
        accountApi.setEntitlements(
          accountId,
          {
            allApis: entDraft.allApis,
            prodEnabled: entDraft.prodEnabled,
            apiCollections: entDraft.apiCollections,
          },
          token
        )
      );
      setEnt(res.data.data);
      setEntDraft(res.data.data);
      message.success("Account entitlements updated.");
    } catch (err) {
      if (!isCancelled(err)) message.error(errorMessage(err));
    } finally {
      setSavingEnt(false);
    }
  };

  const changeBrand = async (brandId) => {
    setSavingBrand(true);
    try {
      const res = await accountApi.setBrand(accountId, brandId ?? null);
      setAccount(res.data.data.account);
      message.success("Brand updated.");
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setSavingBrand(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Order-insensitive diff against the saved allowlist.
  const savedDomains = account?.allowedDomains ?? [];
  const domainsChanged =
    domains.length !== savedDomains.length || domains.some((d) => !savedDomains.includes(d));

  // Order-insensitive diff between the entitlement draft and what's saved.
  const entChanged =
    !!ent &&
    !!entDraft &&
    (ent.allApis !== entDraft.allApis ||
      ent.prodEnabled !== entDraft.prodEnabled ||
      ent.apiCollections.length !== entDraft.apiCollections.length ||
      entDraft.apiCollections.some((n) => !ent.apiCollections.includes(n)));

  const saveDomains = async () => {
    setSavingDomains(true);
    try {
      const res = await withStepUp((token) =>
        accountApi.setAllowedDomainsById(accountId, domains, token)
      );
      const updated = res.data.data.account;
      setAccount(updated);
      setDomains(updated.allowedDomains ?? []);
      message.success("Allowed domains updated.");
    } catch (err) {
      if (!isCancelled(err)) message.error(errorMessage(err));
    } finally {
      setSavingDomains(false);
    }
  };

  return (
    <Space vertical size="middle" style={{ width: "100%" }}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        style={{ paddingInline: 0 }}
        onClick={() => navigate("/console/accounts")}
      >
        Back to Accounts
      </Button>

      <Card>
        {!account ? (
          <Skeleton active paragraph={{ rows: 1 }} />
        ) : (
          <Space vertical size={12} style={{ width: "100%" }}>
            <Space align="center" wrap>
              <Title level={4} style={{ margin: 0 }}>
                {account.name}
              </Title>
              <Tag color={account.isActive ? "green" : "red"}>
                {account.isActive ? "active" : "inactive"}
              </Tag>
              <Tag color={account.type === "internal" ? "geekblue" : "default"}>
                {account.type === "internal" ? "internal" : "external"}
              </Tag>
              <Text type="secondary">Brand:</Text>
              <Select
                value={account.brandId ?? null}
                onChange={changeBrand}
                loading={savingBrand}
                style={{ minWidth: 180 }}
                placeholder="Default (VerifyU)"
                options={[
                  { value: null, label: "Default (VerifyU)" },
                  ...brands.map((b) => ({
                    value: b.id,
                    label: `${b.displayName}${b.isActive ? "" : " (inactive)"}`,
                  })),
                ]}
              />
            </Space>
            <div style={{ maxWidth: 520 }}>
              <Text type="secondary">
                Allowed email domains — invites are rejected unless the invitee's domain is
                on this list. Leave empty to allow any.
              </Text>
              <Select
                mode="tags"
                value={domains}
                onChange={(vals) => setDomains(normalizeDomains(vals))}
                style={{ width: "100%", marginTop: 8 }}
                placeholder="e.g. acme.com"
                tokenSeparators={[",", " "]}
                open={false}
                disabled={savingDomains}
              />
              <div style={{ marginTop: 8 }}>
                <Space>
                  <Button
                    type="primary"
                    onClick={saveDomains}
                    loading={savingDomains}
                    disabled={!domainsChanged}
                  >
                    Save domains
                  </Button>
                  {domainsChanged && (
                    <Button onClick={() => setDomains(savedDomains)} disabled={savingDomains}>
                      Reset
                    </Button>
                  )}
                </Space>
              </div>
            </div>
          </Space>
        )}
      </Card>

      {/* VER-222: what this account is entitled to — the cap for what its admins may grant
          to members. Internal-admin only; the save is step-up-gated. */}
      {account && entDraft && (
        <Card
          title={<Title level={5} style={{ margin: 0 }}>API entitlements</Title>}
          size="small"
        >
          <Space vertical size={12} style={{ width: "100%", maxWidth: 620 }}>
            <Text type="secondary">
              What this account is allowed to use. Account admins can only grant their members
              APIs (and production access) from within this set.
            </Text>

            <Space align="center">
              <Switch
                checked={entDraft.prodEnabled}
                disabled={savingEnt}
                onChange={(v) => setEntDraft((d) => ({ ...d, prodEnabled: v }))}
              />
              <Text>Production access</Text>
              <Text type="secondary">— when off, members can never be given prod.</Text>
            </Space>

            <Space align="center">
              <Switch
                checked={entDraft.allApis}
                disabled={savingEnt}
                onChange={(v) => setEntDraft((d) => ({ ...d, allApis: v }))}
              />
              <Text>All APIs</Text>
              <Text type="secondary">— entitled to every API, including ones added later.</Text>
            </Space>

            {!entDraft.allApis && (
              <div>
                <Text type="secondary">Entitled APIs</Text>
                <Select
                  mode="multiple"
                  value={entDraft.apiCollections}
                  onChange={(vals) => setEntDraft((d) => ({ ...d, apiCollections: vals }))}
                  style={{ width: "100%", marginTop: 8 }}
                  placeholder="Select the APIs this account is entitled to"
                  options={catalog.map((n) => ({ value: n, label: n }))}
                  disabled={savingEnt}
                  allowClear
                  maxTagCount="responsive"
                />
              </div>
            )}

            <Space>
              <Button
                type="primary"
                onClick={saveEntitlements}
                loading={savingEnt}
                disabled={!entChanged}
              >
                Save entitlements
              </Button>
              {entChanged && (
                <Button onClick={() => setEntDraft(ent)} disabled={savingEnt}>
                  Reset
                </Button>
              )}
            </Space>
          </Space>
        </Card>
      )}

      {/* Step-up (re-OTP) modal for the domain + entitlement edits */}
      {modal}

      {/* Members + Invites, scoped to this company. Invites created here land here. */}
      <Tabs
        items={[
          {
            key: "members",
            label: "Members",
            children: <ConsoleUsers accountId={accountId} />,
          },
          {
            key: "invites",
            label: "Invites",
            children: <ConsoleInvites accountId={accountId} accountType={account?.type} />,
          },
        ]}
      />
    </Space>
  );
}
